import React, { useEffect, useMemo } from 'react'
import Intercom, {
  boot as intercomBoot,
  shutdown as intercomShutdown,
  update as intercomUpdate,
} from '@intercom/messenger-js-sdk'
import { useRouter } from 'next/router'

import useAuth from 'lib/hooks/useAuth'

import { getDsUserId } from 'lib/utils/auth'
import { getBrowserCookie, logError } from 'lib/utils'
import { useGlobalContext } from 'lib/context/global-context'

import { COOKIES_DS_SESSION_ID } from 'lib/constants'
import {
  ARTICLE_COUNTRY_ROUTE,
  ARTICLE_DESTINATION_ROUTE,
  ARTICLE_DETAIL_ROUTE,
  ARTICLE_HOME_ROUTE,
  BOOKING_DETAILS_ROUTE,
  ITINERARY_SLUG_ROUTE,
  ORDER_PAYMENT_ROUTE,
  ORDER_STATUS_ROUTE,
} from 'lib/constants/routes'

import { useIntercomToken } from './useIntercomToken'

export const IntercomWidget: React.FC = () => {
  const { isMobileView } = useGlobalContext()
  const { pathname } = useRouter()
  const { user, isLoggedIn } = useAuth()
  const { intercomToken } = useIntercomToken({ isLoggedIn })

  const dsSessionId = getBrowserCookie(COOKIES_DS_SESSION_ID) ?? ''

  const hideChatRoutes = useMemo(
    () => [
      BOOKING_DETAILS_ROUTE.substring(0, BOOKING_DETAILS_ROUTE.length - 1),
      ORDER_PAYMENT_ROUTE.substring(0, ORDER_PAYMENT_ROUTE.length - 1),
      ORDER_STATUS_ROUTE.substring(0, ORDER_STATUS_ROUTE.length - 1),
      ITINERARY_SLUG_ROUTE.substring(0, ITINERARY_SLUG_ROUTE.length - 1),
      ...(isMobileView
        ? [
            ARTICLE_HOME_ROUTE.substring(0, ARTICLE_HOME_ROUTE.length - 1),
            ARTICLE_DESTINATION_ROUTE.substring(0, ARTICLE_DESTINATION_ROUTE.length - 1),
            ARTICLE_COUNTRY_ROUTE.substring(0, ARTICLE_DESTINATION_ROUTE.length - 1),
            ARTICLE_DETAIL_ROUTE.substring(0, ARTICLE_DETAIL_ROUTE.length - 1),
          ]
        : []),
    ],
    [isMobileView]
  )

  useEffect(() => {
    const defaultHideIcon = isMobileView || hideChatRoutes.includes(pathname)

    Intercom({
      app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID || '',
      hide_default_launcher: defaultHideIcon,
    })
  }, [isMobileView, hideChatRoutes, pathname])

  const dsUserId = useMemo(() => {
    if (isLoggedIn && user?.dsUserId) {
      return user.dsUserId
    }

    return getDsUserId()
  }, [isLoggedIn, user?.dsUserId])

  const customerId = useMemo(() => {
    if (isLoggedIn && user?.customerId) {
      return user.customerId
    }

    return ''
  }, [isLoggedIn, user?.customerId])

  useEffect(() => {
    // Clear Intercom on auth state change(signin/signout) to reload in correct mode
    if (window.Intercom) {
      intercomShutdown()
    }

    // This call is required to initialize the Intercom SDK
    Intercom({
      app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID || '',
      hide_default_launcher: isMobileView || hideChatRoutes.includes(pathname),
    })

    // Delay to ensure Intercom is initialized
    setTimeout(() => {
      try {
        if (isLoggedIn) {
          // Logged in user - Boot call is required to start the user session
          intercomBoot({
            app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID || '',
            intercom_user_jwt: intercomToken,
            user_id: String(customerId), // ensure string
            auth_tokens: {
              security_token: intercomToken,
            },
          })
        } else {
          // Guest user - Boot is required to start the user session
          intercomBoot({
            app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID || '',
          })
        }

        setTimeout(() => {
          // Call this after some delay to ensure the user is fully initialized
          intercomUpdate({
            pelago_session_id: dsSessionId,
            pelago_ds_user_id: dsUserId,
          })
        }, 100)
      } catch (error) {
        logError(error)
      }
    }, 1000)

    // Clean up Intercom when component unmounts
    return () => {
      try {
        if (window.Intercom) {
          intercomShutdown()
        }
      } catch (error) {
        logError(error)
      }
    }
  }, [dsSessionId, dsUserId, intercomToken, isLoggedIn, customerId, isMobileView, hideChatRoutes, pathname])

  return null // Intercom renders its own UI
}
